import React, { Suspense } from 'react';
import { Routes, Route, NavLink, Navigate, Outlet } from 'react-router-dom';

import { WorkflowsOverview } from './features/workflows/components/WorkflowsOverview';
import { BatchWorkspace } from './features/workflows/components/batches/BatchWorkspace';
import { isPilotPipelineEnabled } from './features/workflows/config/pipelines';

import logo from "./assets/company-logo.png";
import ProtectedRoute from "./auth/ProtectedRoute";
import { useAuth } from "./auth/AuthContext";
import LoginPage from "./pages/LoginPage";
import CreditProfilePage from "./features/credits/CreditProfilePage";

const PressurePage = React.lazy(() =>
  import('./features/pressureAnalysis/components/PressurePage')
);
const StabilityPlannerPage = React.lazy(() =>
  import('./features/stabilityPlanner/components/StabilityPlannerPage')
);
const RiboGreenPage = React.lazy(() =>
  import('./features/riboGreen/components/RiboGreenPage')
);
const MappingPage = React.lazy(() =>
  import('./features/mapping').then((module) => ({ default: module.MappingPage }))
);
const LnpPredictorPage = React.lazy(() =>
  import('./features/lnpPredictor/components/LnpPredictorPage')
);

function PilotPipelineRoute({ pipelineId, children }) {
  if (!isPilotPipelineEnabled(pipelineId)) {
    return <Navigate to="/workflows" replace />;
  }

  return (
    <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}>
      {children}
    </Suspense>
  );
}


function ProtectedLayout() {
  const { logout, user } = useAuth();

  return (
    <div className="container mx-auto p-6">
      <header className="mb-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <img
          src={logo}
          alt="Company logo"
          className="h-12 w-auto"
        />
        <nav className="flex flex-wrap items-center gap-6 text-base font-medium text-slate-700">
          <NavLink
            to="/workflows"
            className={({ isActive }) =>
              isActive ? 'text-slate-900 underline' : 'hover:text-slate-900 hover:underline'
            }
          >
            Workflows
          </NavLink>
          <NavLink
            to="/workflow/batches"
            className={({ isActive }) =>
              isActive ? 'text-slate-900 underline' : 'hover:text-slate-900 hover:underline'
            }
          >
            Batches
          </NavLink>
          {isPilotPipelineEnabled('lnp') && (
            <NavLink
              to="/lnp"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 underline' : 'hover:text-slate-900 hover:underline'
              }
            >
              LNP predictor
            </NavLink>
          )}

        </nav>
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <NavLink to="/profile" className="hover:underline">{user?.username}</NavLink>
          <button
            type="button"
            onClick={logout}
            className="rounded border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900"
          >
            Logout
          </button>
        </div>
      </header>


      <Outlet />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<ProtectedLayout />}>
          {/* Redirect root to Workflows */}
          <Route path="/" element={<Navigate to="/workflows" replace />} />

          {/* Pressure analysis page */}
          <Route
            path="/pressure"
            element={
              <PilotPipelineRoute pipelineId="pressure">
                <PressurePage />
              </PilotPipelineRoute>
            }
          />

          {/* Workflows dashboard */}
          <Route path="/workflows" element={<WorkflowsOverview />} />
          <Route path="/workflow/batches" element={<BatchWorkspace />} />
          <Route
            path="/lnp"
            element={
              <PilotPipelineRoute pipelineId="lnp">
                <LnpPredictorPage />
              </PilotPipelineRoute>
            }
          />
          <Route path="/profile" element={<CreditProfilePage />} />

          {/* Stability */}
          <Route
            path="/stability"
            element={
              <PilotPipelineRoute pipelineId="stability">
                <StabilityPlannerPage />
              </PilotPipelineRoute>
            }
          />

          {/* RiboGreen */}
          <Route
            path="/ribo-green"
            element={
              <PilotPipelineRoute pipelineId="ribo-green">
                <RiboGreenPage />
              </PilotPipelineRoute>
            }
          />
          {/* LC-MS Mapping */}
          <Route
            path="/mapping"
            element={
              <PilotPipelineRoute pipelineId="mapping">
                <MappingPage />
              </PilotPipelineRoute>
            }
          />
          {/* Fallback to Workflows */}
          <Route path="*" element={<Navigate to="/workflows" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
